import { useState, useEffect, useMemo, useCallback } from "react";
import { apiRequest, API_BASE_URL } from "../config/api";

/**
 * @param {Object} filters - Product list filters (page, limit, sort, categories, tags, etc.)
 * @param {{ section?: 'accessories' | 'yarns' }} options - When section is 'accessories', calls GET /products/accessories; when 'yarns', calls GET /products?exclude_category_slug=accessories
 */
export const useProducts = (filters = {}, options = {}) => {
  const { section } = options;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    limit: filters.limit || 24,
  });

  const memoizedFilters = useMemo(() => {
    const result = {};
    const skipCategories = section === "accessories";
    Object.entries(filters).forEach(([key, value]) => {
      if (skipCategories && key === "categories") return;
      if (value !== null && value !== undefined && value !== "") {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            result[key] = value;
          }
        } else if (typeof value === "boolean") {
          if (value) {
            result[key] = value;
          }
        } else {
          result[key] = value;
        }
      }
    });
    return JSON.stringify(result);
  }, [filters, section]);

  const fetchProducts = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      try {
        const parsedFilters = JSON.parse(memoizedFilters);
        const queryParams = new URLSearchParams();

        queryParams.set("page", page.toString());
        queryParams.set("limit", (filters.limit || 24).toString());

        Object.entries(parsedFilters).forEach(([key, value]) => {
          if (key === "page" || key === "limit") return;
          if (Array.isArray(value)) {
            value.forEach((v) => queryParams.append(key, v));
          } else {
            queryParams.set(key, value.toString());
          }
        });

        let url;
        if (section === "accessories") {
          url = `/products/accessories?${queryParams.toString()}`;
        } else if (section === "yarns") {
          queryParams.set("exclude_category_slug", "accessories");
          url = `/products?${queryParams.toString()}`;
        } else {
          url = `/products?${queryParams.toString()}`;
        }

        const data = await apiRequest(url);
        const productList = Array.isArray(data?.data) ? data.data : [];
        const paginationData = data?.pagination || {};

        setProducts(productList);
        setPagination({
          currentPage: paginationData.currentPage || 1,
          totalPages: paginationData.totalPages || 1,
          totalProducts: paginationData.totalProducts || productList.length,
          limit: paginationData.limit || filters.limit || 24,
        });
      } catch (err) {
        console.error("Product fetch error:", err);
        setError(err.message || "Failed to fetch products");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [memoizedFilters, filters.limit, section]
  );

  const refreshProducts = useCallback(() => {
    fetchProducts(1);
  }, [fetchProducts]);

  useEffect(() => {
    let page = 1;
    try {
      const parsed = JSON.parse(memoizedFilters);
      if (parsed.page != null && parsed.page !== "") {
        const n = Number(parsed.page);
        if (Number.isFinite(n) && n >= 1) page = Math.floor(n);
      }
    } catch {
      /* keep page 1 */
    }
    fetchProducts(page);
  }, [memoizedFilters, fetchProducts]);

  useEffect(() => {
    const handleProductListRefresh = () => {
      fetchProducts(1);
    };

    window.addEventListener("productListRefresh", handleProductListRefresh);

    return () => {
      window.removeEventListener(
        "productListRefresh",
        handleProductListRefresh
      );
    };
  }, [fetchProducts]);

  return {
    products,
    loading,
    error,
    pagination,
    fetchProducts,
    refreshProducts,
  };
};

export const useProduct = (slug) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const base = (API_BASE_URL || "https://api.knotsandkrafts.in/api").replace(
        /\/+$/,
        ""
      );

      const response = await fetch(`${base}/products/${slug}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError("Product not found");
          setProduct(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        setProduct(data.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Product fetch error:", err);
      setError(err.message || "Failed to fetch product");
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useEffect(() => {
    const handleProductDetailRefresh = (event) => {
      const { productId } = event.detail || {};
      if (!productId || productId === product?.id) {
        fetchProduct();
      }
    };

    window.addEventListener("productDetailRefresh", handleProductDetailRefresh);

    return () => {
      window.removeEventListener(
        "productDetailRefresh",
        handleProductDetailRefresh
      );
    };
  }, [product?.id, slug, fetchProduct]);

  const refreshProduct = useCallback(() => {
    fetchProduct();
  }, [fetchProduct]);

  return {
    product,
    loading,
    error,
    refreshProduct,
  };
};

export const useFeaturedProducts = (limit = 4) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFeaturedProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiRequest(`/products/featured?limit=${limit}`);
      const featuredList = Array.isArray(data?.data) ? data.data : [];
      setProducts(featuredList);
    } catch (err) {
      console.error("Featured products fetch error:", err);
      setError(err.message || "Failed to fetch featured products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  return {
    products,
    loading,
    error,
    refreshProducts: fetchFeaturedProducts,
  };
};
